<template>
    <div>
        <div class="table-responsive">
            <table class="table table-hover table-vertical-center table-head-bg">
                <thead>
                    <tr>
                        <th>
                            <TableSort field="name" :value="sortOptions.initialSortBy" @input="sortUser">
                                {{ __('general.user') }}
                            </TableSort>
                        </th>
                        <th class="text-center">
                            <TableSort field="role" :value="sortOptions.initialSortBy" @input="sortRole">
                                {{ __('user.role') }}
                            </TableSort>
                        </th>
                        <th class="text-center">
                            <TableSort field="created_at" :value="sortOptions.initialSortBy" @input="sortUserCreated">
                                {{ __('user.registration_date') }}
                            </TableSort>
                        </th>
                        <th class="text-center">
                            <TableSort field="last_login_at" :value="sortOptions.initialSortBy" @input="sortUserLastLogin">
                                {{ __('user.last_login') }}
                            </TableSort>
                        </th>
                        <th class="text-right user-select-none">{{ __('general.actions') }}</th>
                    </tr>
                </thead>
                <tbody>
                <tr v-for="user in rows" :key="user.id">
                    <td>
                        <!-- Invitations -->
                        <user :user="user"></user>
                    </td>
                    <td>
                        <!-- Role -->
                        <store-role :role="user.role"></store-role>
                    </td>

                    <!-- Dates -->
                    <td>
                        <date-time :timestamp="user.created_at"></date-time>
                    </td>
                    <td>
                        <date-time :timestamp="user.last_login_at"></date-time>
                    </td>
                    <td>
                        <!-- Actions -->
                        <actions>
                            <action-button v-if="Permission.EDIT_USERS"
                                           :href="$route('user.edit', [user.url_key])"
                                           class="btn-hover-primary"
                                           icon="fas fa-pencil-alt"
                                           title="Edit user">
                            </action-button>
                            <action-button v-if="Permission.CHANGE_STORE_OWNER && user.role && user.role.id !== 1"
                                           @click="changeOwner(user)"
                                           class="btn-hover-info"
                                           icon="fas fa-house-user"
                                           title="Change ownership">
                            </action-button>
                            <action-button v-if="Permission.REMOVE_STORE_USER && user.role && user.role.id !== 1"
                                           @click="removeUser(user)"
                                           class="btn-hover-danger"
                                           icon="fas fa-user-slash"
                                           title="Remove user">
                            </action-button>
                        </actions>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <add-user-to-store-modal v-if="StorePermission.ADD_USER"
                                 :store-uuid="storeUuid"
                                 :roles="roles"
                                 v-on:userAdded="loadItems"/>
    </div>
</template>

<script>

import Navigation from './Nav/Navigation.vue'
import AddUserToStoreModal from '@components/Store/Includes/AddUserToStoreModal.vue'
import DateTime from '@components/DateTime.vue'
import Actions from '@components/TableComponents/Actions.vue'
import StoreRole from '@components/TableComponents/StoreRole.vue'
import ActionButton from '@components/TableComponents/ActionButton.vue'
import {PermissionsMixin} from '@js/permissions'
import User from '@components/TableComponents/User.vue'
import TableSort from '@components/Table/TableSort.vue'

export default {
    layout: Navigation,

    name: 'EditStoreUsers',

    components: {
        TableSort,
        AddUserToStoreModal,
        DateTime,
        Actions,
        StoreRole,
        User,
        ActionButton,
    },

    mixins: [
        PermissionsMixin,
    ],

    props: {
        storeUuid: String,
        roles: Array,
        users: Array,
    },

    data() {
        return {
            sortOptions: {
                initialSortBy: [
                    {field: 'role', type: 'asc'},
                    {field: 'name', type: 'asc'},
                ],
            },
            rows: [],
        }
    },

    beforeMount() {
        this.requestStorePermissions(this.storeUuid)
        this.loadItems()
    },

    methods: {
        async loadItems() {
            let {data} = await axios.get(this.$route('store.users', [this.storeUuid]))
            this.rows = data.data
        },

        sortRole(x, y, col, rowX, rowY) {
            if (rowX.role === undefined || rowY.role === undefined) {
                return 0
            }

            return rowX.role.id - rowY.role.id
        },

        sortUser(x, y, col, rowX, rowY) {
            return rowX.name.localeCompare(rowY.name)
        },

        sortUserCreated(x, y, col, rowX, rowY) {
            return rowX.created_at.localeCompare(rowY.created_at)
        },

        sortUserLastLogin(x, y, col, rowX, rowY) {
            let lastLoginX = rowX.last_login_at ?? 0
            let lastLoginY = rowY.last_login_at ?? 0
            return lastLoginX.localeCompare(lastLoginY)
        },

        changeOwner(user) {
            Swal.fire({
                title: this.__('store.store_owner.change', {user: user.name}),
                showDenyButton: false,
                showCancelButton: true,
                confirmButtonColor: '#F64E60',
                confirmButtonText: this.__('actions.change_owner'),
                cancelButtonText: this.__('actions.abort'),
                html: this.__('store.store_owner.information'),
            }).then((result) => {
                if (result.isConfirmed) {
                    this.$inertia.post(
                        this.$route('store.change_owner', {storeUuid: this.storeUuid, target: user.id}).url(), {}, {
                            onSuccess: () => {
                                this.loadItems()
                            },
                        })
                }
            })
        },

        removeUser(user) {
            Swal.fire({
                title: __('store.remove_user', {user: user.name}),
                showDenyButton: false,
                showCancelButton: true,
                confirmButtonColor: '#F64E60',
                confirmButtonText: this.__('actions.user.remove'),
                cancelButtonText: this.__('actions.abort'),
                html: `<small>${user.email}</small><br><strong>${user.name}</strong>`,
            }).then((result) => {
                if (result.isConfirmed) {
                    this.$inertia.post(this.$route('store.remove_user', [this.storeUuid, user.id]).url(), {}, {
                        onSuccess: () => {
                            this.loadItems()
                        },
                    })
                }
            })
        },
    },
}
</script>
